import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {useMutation} from '@github-ui/react-query'
import type {ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {useMarkersActions, usePullRequestBasePath} from '../stores/selectors'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

export function useReactToCommentMutation(filePath: string, lineMarkersKey: LineKey | FileKey) {
  const basePath = usePullRequestBasePath()
  const {updateThreadComment} = useMarkersActions()

  const onSuccessCallback = (
    data: {reactionGroups: ReactionViewerGroup[]},
    args: {commentDatabaseId: number; threadId: string; reaction: string; viewerHasReacted: boolean},
  ) => {
    updateThreadComment({
      threadID: parseInt(args.threadId),
      filePath,
      lineMarkersKey,
      match: storedComment => storedComment.databaseId === args.commentDatabaseId,
      transform: oldComment => {
        if (oldComment) {
          return {...oldComment, reactionGroups: data.reactionGroups}
        }
      },
    })
  }

  return useReactToCommentMutationWithCallback(basePath, onSuccessCallback)
}

export function useReactToCommentMutationWithCallback(
  basePath: string,
  onSuccessCallback?: (
    data: {reactionGroups: ReactionViewerGroup[]},
    args: {commentDatabaseId: number; threadId: string; reaction: string; viewerHasReacted: boolean},
  ) => void,
) {
  return useMutation({
    mutationFn: async ({
      commentDatabaseId,
      reaction,
      viewerHasReacted,
    }: {
      commentDatabaseId: number
      threadId: string // Not needed in the request, but needed in the onSuccess
      reaction: string
      viewerHasReacted: boolean
    }) => {
      const apiUrl = `${basePath}/page_data/${
        viewerHasReacted ? PageData.removeCommentReaction : PageData.addCommentReaction
      }`

      const response = await fetchWithErrorHandling(apiUrl, {
        method: 'POST',
        body: {
          reaction,
          commentId: commentDatabaseId,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: onSuccessCallback,
  })
}
